import { useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  describeOrganizationCorrectionError,
  setCaptureOrganizationFields,
} from '../../data/writers/set-capture-organization';
import { requestCaptureOrganization } from '../../data/organize-client';
import { reportError } from '../../lib/monitoring';
import type { CaptureOrganizationRow } from '../../data/database.types';
import './captures.css';

/**
 * WHAT CREWLINK WORKED OUT, AND HOW BRANDON FIXES IT — M7 / S07-03.
 *
 * Automatic Organization has been deriving a location and a caption since
 * S07-02 and **nobody could see any of it**. This is the surface for it, and
 * for the one thing a derived value always needs: a way to be wrong in public
 * and corrected in one tap.
 *
 * **THE TRANSCRIPT IS NOT HERE, AND THAT IS DELIBERATE.** The caption is a
 * derived summary; the transcript is what Brandon actually said. They live in
 * separate sections with different weight so the derived one can never be
 * mistaken for source truth (PT §17).
 *
 * **ACCEPTED VALUES SURVIVE A LATER ATTEMPT.** `state` describes the CURRENT
 * attempt, not the data: a Capture that organized yesterday and is being
 * re-organized today still shows yesterday's location while it runs, and still
 * shows it if today's attempt fails. Blanking a good answer because a new one
 * is in flight would be a worse lie than a slightly old one.
 *
 * **NOTHING HERE ORGANIZES ON RENDER.** Every provider call comes from a
 * deliberate tap — Organize or Retry — which is why opening a Capture is free.
 */

/** Shown when the model ran but could not safely place the Capture. */
const NEEDS_REVIEW_LABEL = 'Needs review';

export function CaptureOrganizationSection({
  captureId,
  organization,
  onChanged,
}: {
  captureId: string;
  /** Server truth, or null when organization has never run for this Capture. */
  organization: CaptureOrganizationRow | null;
  /** Reload Capture + History. Awaited, so nothing shows as saved before it is. */
  onChanged: () => Promise<void>;
}) {
  const client = getSupabaseClient();
  const [editing, setEditing] = useState(false);
  const [locationDraft, setLocationDraft] = useState('');
  const [captionDraft, setCaptionDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = organization?.location_display ?? null;
  const caption = organization?.caption ?? null;
  const state = organization?.state ?? null;

  // The accepted values are shown in every state. Only the CONTROLS change.
  const organizing = state === 'pending';
  const needsReview = state === 'needs_review';
  // RETRY IS FOR `failed` ONLY. `needs_review` means the model ran fine and
  // honestly found no location — re-asking the same transcript would spend a
  // call to receive the same answer, so that state offers Edit instead.
  const canRetry = state === 'failed';
  const canOrganize = organization === null;

  const startEditing = (): void => {
    setLocationDraft(location ?? '');
    setCaptionDraft(caption ?? '');
    setError(null);
    setEditing(true);
  };

  const cancelEditing = (): void => {
    setEditing(false);
    setLocationDraft('');
    setCaptionDraft('');
    setError(null);
  };

  const save = async (): Promise<void> => {
    if (busy || !client) return;
    setBusy(true);
    setError(null);
    try {
      // BOTH FIELDS ARE ALWAYS SENT, and that is safe precisely because the
      // DATABASE decides what actually changed. An untouched field compares
      // equal, so it is not written, does not become human-owned, and produces
      // no history — the screen does not have to be clever, and a screen that
      // had to be clever about this is how the original defect happened.
      //
      // An empty box is a deliberate clear, not an accident: `null` here means
      // "there is no location", which is a real correction.
      await setCaptureOrganizationFields(client, captureId, {
        location: { value: locationDraft.trim() === '' ? null : locationDraft },
        caption: { value: captionDraft.trim() === '' ? null : captionDraft },
      });
      await onChanged();
      setEditing(false);
      setLocationDraft('');
      setCaptionDraft('');
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      reportError(caught, 'capture.correctOrganization');
      setError(describeOrganizationCorrectionError(message));
      // HIS WORDS STAY IN THE BOX on a failure. Losing a correction to a lost
      // connection would be the second thing to go wrong, not the first.
    } finally {
      setBusy(false);
    }
  };

  /**
   * Organize and Retry are the SAME endpoint — the one that owns admission and
   * therefore owns whether a model call is spent. There is no second reasoning
   * path, and the browser never touches the AI writers.
   */
  const request = async (explicitRetry: boolean): Promise<void> => {
    if (busy || !client) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requestCaptureOrganization(
        captureId,
        explicitRetry ? { explicitRetry: true } : {},
      );
      if (result.status === 'unavailable') {
        setError('Could not reach Crewlink. Check your connection and try again.');
      }
      // The row is what tells the truth, so reload either way — including after
      // a refusal, where the reason is already recorded in the row.
      await onChanged();
    } catch (caught) {
      reportError(caught, 'capture.organize');
      setError('Could not reach Crewlink. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="capture-organization" aria-labelledby="capture-organization-heading">
      <div className="capture-note-labelrow">
        <h2 id="capture-organization-heading" className="projects-group-heading">
          WHAT CREWLINK WORKED OUT
        </h2>
        {!editing && !canOrganize ? (
          <button
            type="button"
            className="capture-note-editbutton"
            // NAMED FOR A SCREEN READER, not just for the eye. The transcript
            // section directly above carries its own `Edit`, and two controls
            // announced identically as "Edit button" on one screen is a real
            // defect for anyone not looking at the layout. The visible word
            // stays `Edit` — the design export's small, quiet control — and
            // only the accessible name says which Edit this is.
            aria-label="Edit location and caption"
            onClick={startEditing}
            disabled={busy}
          >
            Edit
          </button>
        ) : null}
      </div>

      {editing ? (
        <>
          <label className="sheet-label" htmlFor="capture-organization-location">
            Location
          </label>
          <input
            id="capture-organization-location"
            className="capture-organization-input"
            type="text"
            value={locationDraft}
            onChange={(event) => {
              setLocationDraft(event.target.value);
            }}
            disabled={busy}
            placeholder="Unit 1207 Living Room"
          />
          <label className="sheet-label" htmlFor="capture-organization-caption">
            Caption
          </label>
          <input
            id="capture-organization-caption"
            className="capture-organization-input"
            type="text"
            value={captionDraft}
            onChange={(event) => {
              setCaptionDraft(event.target.value);
            }}
            disabled={busy}
            placeholder="Cracked drywall by the window."
          />
          <p className="capture-create-hint">
            Clearing a box removes that value. Your correction is kept, and Crewlink will not
            overwrite it.
          </p>
          <div className="capture-transcript-actions">
            {/* Distinct accessible names, for the same reason as Edit above:
                the transcript can be open for editing at the same moment, and
                two identical `Save` buttons is the worst possible ambiguity to
                leave on a screen that writes to a record. */}
            <button
              type="button"
              className="capture-transcript-save"
              aria-label="Save location and caption"
              onClick={() => {
                void save();
              }}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="capture-note-editbutton"
              aria-label="Cancel editing location and caption"
              onClick={cancelEditing}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          {caption !== null ? (
            <p className="capture-organization-caption">{caption}</p>
          ) : (
            <p className="projects-empty-body">
              {canOrganize
                ? 'Crewlink has not organized this Capture yet.'
                : 'No caption for this Capture.'}
            </p>
          )}

          <div className="capture-organization-state">
            {/* A quiet line, not a spinner: something is happening and nothing
                is wrong. The accepted values above stay on screen throughout. */}
            {organizing ? <span className="capture-organization-working">Organizing…</span> : null}
            {needsReview ? (
              <span className="capture-organization-review">{NEEDS_REVIEW_LABEL}</span>
            ) : null}
            {canRetry ? (
              <button
                type="button"
                className="capture-note-editbutton"
                onClick={() => {
                  void request(true);
                }}
                disabled={busy}
              >
                {busy ? 'Retrying…' : 'Retry'}
              </button>
            ) : null}
            {canOrganize ? (
              <button
                type="button"
                className="capture-note-editbutton"
                onClick={() => {
                  void request(false);
                }}
                disabled={busy}
              >
                {busy ? 'Organizing…' : 'Organize'}
              </button>
            ) : null}
          </div>
        </>
      )}

      {error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
